import DownloadIcon from "@mui/icons-material/Download";
import LinkIcon from "@mui/icons-material/Link";
import PrintIcon from "@mui/icons-material/Print";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  GlobalStyles,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BarcodePreview } from "./barcode-preview";
import {
  BARCODE_FORMAT_OPTIONS,
  DEFAULT_SETTINGS,
  FONT_OPTIONS,
  SERIAL_BOUNDS,
  buildShareUrl,
  downloadBarcodePng,
  generateSerializedValues,
  loadInitialSettings,
  parseValues,
  persistSettings,
  type BarcodeFormat,
  type BarcodeRenderOptions,
  type BarcodeSettings,
} from "./barcode-generator-utils";

const styles = {
  root: { p: 3, px: 5 },
  layout: { alignItems: "flex-start" },
  controlsPanel: { width: 320, flexShrink: 0, p: 2 },
  actions: { flexWrap: "wrap" },
  grid: { flex: 1, minWidth: 0 },
  emptyState: { color: "text.secondary", p: 4, textAlign: "center" },
  serialPaper: { p: 1.5, bgcolor: "action.hover" },
  serialCount: { width: 96, flexShrink: 0 },
} satisfies Record<string, SxProps<Theme>>;

const printGlobalStyles = {
  "@media print": {
    "body *": { visibility: "hidden" },
    ".barcode-print-area, .barcode-print-area *": { visibility: "visible" },
    ".barcode-print-area": {
      position: "absolute",
      left: 0,
      top: 0,
      width: "100%",
    },
    ".no-print": { display: "none !important" },
  },
} as const;

/**
 * Bulk barcode generator for internal testing.
 *
 * Mirrors the free-barcode.com bulk generator: paste many values, tune the
 * symbology and presentation, preview a live grid, print a sheet, download
 * individual PNGs, and share the exact configuration via URL. Settings are
 * hydrated from the URL query string (a shared link) or `localStorage`, and
 * persisted back to `localStorage` on every change. No backend is involved.
 */
const BarcodeGenerator = () => {
  const [settings, setSettings] =
    useState<BarcodeSettings>(loadInitialSettings);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [serialBase, setSerialBase] = useState("");
  const [serialCount, setSerialCount] = useState(10);

  useEffect(() => {
    persistSettings(settings);
  }, [settings]);

  const parsedValues = useMemo(
    () => parseValues(settings.values),
    [settings.values],
  );

  // Values may repeat legitimately, so pair each with a positional key rather
  // than using the bare value (which would collide across duplicate lines).
  const keyedValues = useMemo(
    () =>
      parsedValues.map((value, index) => ({ value, key: `${value}#${index}` })),
    [parsedValues],
  );

  // Memoized on the render-affecting fields only (not `values`/`columns`), so a
  // stable reference reaches every grid cell and keeps BarcodePreview's memo.
  const renderOptions = useMemo<BarcodeRenderOptions>(
    () => ({
      format: settings.format,
      width: settings.width,
      height: settings.height,
      fontSize: settings.fontSize,
      font: settings.font,
      displayValue: settings.displayValue,
      margin: settings.margin,
      lineColor: settings.lineColor,
      background: settings.background,
    }),
    [
      settings.format,
      settings.width,
      settings.height,
      settings.fontSize,
      settings.font,
      settings.displayValue,
      settings.margin,
      settings.lineColor,
      settings.background,
    ],
  );

  /**
   * Updates a single settings field, preserving the rest of the configuration.
   *
   * @param key - The settings field to update.
   * @param value - The new value for `key`.
   */
  const updateField = useCallback(
    <K extends keyof BarcodeSettings>(key: K, value: BarcodeSettings[K]) => {
      setSettings((previous) => ({ ...previous, [key]: value }));
    },
    [],
  );

  const handleCopyLink = useCallback(async () => {
    await navigator.clipboard.writeText(buildShareUrl(settings));
    setSnackbarOpen(true);
  }, [settings]);

  const handleDownloadAll = useCallback(() => {
    for (const value of parsedValues) {
      try {
        downloadBarcodePng(value, renderOptions);
      } catch {
        // Skip values invalid for the selected symbology.
      }
    }
  }, [parsedValues, renderOptions]);

  /**
   * Generates a serialized batch from the base value and appends it to the
   * existing values, then clears the base input.
   */
  const handleAddSerialized = useCallback(() => {
    const generated = generateSerializedValues(serialBase, serialCount);
    if (generated.length === 0) {
      return;
    }
    setSettings((previous) => {
      const existing = previous.values.trimEnd();
      const separator = existing.length > 0 ? "\n" : "";
      return {
        ...previous,
        values: `${existing}${separator}${generated.join("\n")}`,
      };
    });
    setSerialBase("");
  }, [serialBase, serialCount]);

  const handleReset = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  const gridSx = useMemo<SxProps<Theme>>(
    () => ({
      display: "grid",
      gridTemplateColumns: `repeat(${settings.columns}, 1fr)`,
      gap: 2,
    }),
    [settings.columns],
  );

  return (
    <Stack sx={styles.root} spacing={2}>
      <GlobalStyles styles={printGlobalStyles} />
      <Box className="no-print">
        <Typography variant="h5">Bulk barcode generator</Typography>
        <Typography variant="body2" color="text.secondary">
          Paste one value per line. Share the link to reproduce this exact set.
        </Typography>
      </Box>

      <Stack direction="row" spacing={3} sx={styles.layout}>
        <Paper
          variant="outlined"
          className="no-print"
          sx={styles.controlsPanel}
        >
          <Stack spacing={2}>
            <TextField
              multiline
              fullWidth
              label="Values (one per line)"
              minRows={6}
              maxRows={16}
              value={settings.values}
              placeholder={"barcode-1\nbarcode-2\nbarcode-3"}
              onChange={(event) => {
                updateField("values", event.target.value);
              }}
            />

            <Paper variant="outlined" sx={styles.serialPaper}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">Serialize</Typography>
                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Base value"
                    value={serialBase}
                    placeholder="pallet"
                    onChange={(event) => {
                      setSerialBase(event.target.value);
                    }}
                  />
                  <TextField
                    size="small"
                    label="Count"
                    type="number"
                    sx={styles.serialCount}
                    inputProps={{
                      min: SERIAL_BOUNDS.count.min,
                      max: SERIAL_BOUNDS.count.max,
                    }}
                    value={serialCount}
                    onChange={(event) => {
                      setSerialCount(Number(event.target.value));
                    }}
                  />
                </Stack>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={serialBase.trim().length === 0}
                  onClick={handleAddSerialized}
                >
                  Add {Math.max(1, serialCount)} (e.g.{" "}
                  {serialBase.trim().length > 0 ? serialBase.trim() : "base"}
                  +001)
                </Button>
              </Stack>
            </Paper>

            <TextField
              select
              fullWidth
              label="Format"
              value={settings.format}
              onChange={(event) => {
                updateField("format", event.target.value as BarcodeFormat);
              }}
            >
              {BARCODE_FORMAT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="Bar width"
                type="number"
                value={settings.width}
                onChange={(event) => {
                  updateField("width", Number(event.target.value));
                }}
              />
              <TextField
                fullWidth
                label="Bar height"
                type="number"
                value={settings.height}
                onChange={(event) => {
                  updateField("height", Number(event.target.value));
                }}
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="Font size"
                type="number"
                value={settings.fontSize}
                onChange={(event) => {
                  updateField("fontSize", Number(event.target.value));
                }}
              />
              <TextField
                fullWidth
                label="Columns"
                type="number"
                value={settings.columns}
                onChange={(event) => {
                  updateField("columns", Number(event.target.value));
                }}
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                select
                fullWidth
                label="Font"
                value={settings.font}
                onChange={(event) => {
                  updateField("font", event.target.value);
                }}
              >
                {FONT_OPTIONS.map((font) => (
                  <MenuItem key={font} value={font}>
                    {font}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Margin"
                type="number"
                value={settings.margin}
                onChange={(event) => {
                  updateField("margin", Number(event.target.value));
                }}
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="Bar color"
                type="color"
                value={settings.lineColor}
                onChange={(event) => {
                  updateField("lineColor", event.target.value);
                }}
              />
              <TextField
                fullWidth
                label="Background"
                type="color"
                value={settings.background}
                onChange={(event) => {
                  updateField("background", event.target.value);
                }}
              />
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={settings.displayValue}
                  onChange={(event) => {
                    updateField("displayValue", event.target.checked);
                  }}
                />
              }
              label="Show text under barcode"
            />

            <Divider />

            <Stack direction="row" spacing={1} sx={styles.actions}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<LinkIcon />}
                onClick={handleCopyLink}
              >
                Copy link
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PrintIcon />}
                disabled={parsedValues.length === 0}
                onClick={() => {
                  globalThis.print();
                }}
              >
                Print
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                disabled={parsedValues.length === 0}
                onClick={handleDownloadAll}
              >
                PNGs
              </Button>
              <Button
                variant="text"
                size="small"
                color="inherit"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
              >
                Reset
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Box sx={styles.grid}>
          {parsedValues.length === 0 ? (
            <Typography className="no-print" sx={styles.emptyState}>
              Enter one or more values to generate barcodes.
            </Typography>
          ) : (
            <Box className="barcode-print-area" sx={gridSx}>
              {keyedValues.map((item) => (
                <BarcodePreview
                  key={item.key}
                  value={item.value}
                  options={renderOptions}
                />
              ))}
            </Box>
          )}
        </Box>
      </Stack>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2500}
        message="Shareable link copied to clipboard"
        onClose={() => {
          setSnackbarOpen(false);
        }}
      />
    </Stack>
  );
};

export default BarcodeGenerator;
